import { useState, useEffect, useCallback } from 'react';
import { BeaconWallet } from '@taquito/beacon-wallet';
import { BeaconEvent } from '@airgap/beacon-dapp';
import { TezosToolkit } from '@taquito/taquito';
import { createBeaconWallet, resetWalletForNetwork, NETWORK_CONFIG, type NetworkOption } from '@/lib/tezos';
import { useAppNetwork } from '@/hooks/useAppNetwork';

export type { NetworkOption } from '@/lib/tezos';

interface WalletState {
  isConnected: boolean;
  address: string | null;
  balance: string | null;
  network: string | null;
  tezos: TezosToolkit | null;
  wallet: BeaconWallet | null;
}

export const useWallet = () => {
  const [state, setState] = useState<WalletState>({
    isConnected: false,
    address: null,
    balance: null,
    network: null,
    tezos: null,
    wallet: null,
  });
  const [isLoading, setIsLoading] = useState(false);
  const { network: appNetwork, setNetwork: setAppNetwork } = useAppNetwork();

  useEffect(() => {
    const initWallet = async () => {
      try {
        console.log('Initializing wallet for network:', appNetwork);
        const config = NETWORK_CONFIG[appNetwork];
        const tezos = new TezosToolkit(config.rpcUrl);
        const wallet = createBeaconWallet(appNetwork);
        tezos.setWalletProvider(wallet);
        console.log('Wallet created for', appNetwork);

        // Subscribe to active account changes (as per Beacon docs)
        // Note: We don't update network here - let switchNetwork handle that
        // to avoid overwriting our NetworkOption with Beacon's type
        wallet.client.subscribeToEvent(BeaconEvent.ACTIVE_ACCOUNT_SET, async (account) => {
          console.log(`${BeaconEvent.ACTIVE_ACCOUNT_SET} triggered:`, account);
          if (account) {
            try {
              const balance = await tezos.tz.getBalance(account.address);
              setState(prev => ({
                ...prev,
                isConnected: true,
                address: account.address,
                balance: balance.toString(),
                // Don't overwrite network - keep what switchNetwork set
              }));
            } catch (err) {
              console.error('Failed to get balance:', err);
            }
          }
        });

        // Check for existing connection on page load
        const activeAccount = await wallet.client.getActiveAccount();
        if (activeAccount) {
          console.log('Found active account:', activeAccount.address);

          // Map Beacon's network type to our NetworkOption
          const beaconType = activeAccount.network?.type;
          let networkOption: NetworkOption = 'mainnet'; // default to mainnet
          if (beaconType === 'mainnet') networkOption = 'mainnet';
          else if (beaconType === 'ghostnet') networkOption = 'ghostnet';
          else if (beaconType === 'custom') networkOption = 'sandbox';

          // If the wallet's network differs from what we initialized with,
          // recreate TezosToolkit with the correct RPC
          let activeTezos = tezos;
          let activeWallet = wallet;
          if (networkOption !== appNetwork) {
            console.log(`Network mismatch: app=${appNetwork}, wallet=${networkOption}. Reinitializing...`);
            const correctConfig = NETWORK_CONFIG[networkOption];
            activeTezos = new TezosToolkit(correctConfig.rpcUrl);
            activeWallet = createBeaconWallet(networkOption);
            activeTezos.setWalletProvider(activeWallet);
          }

          const balance = await activeTezos.tz.getBalance(activeAccount.address);

          // Sync the app network to match the persisted wallet network
          setAppNetwork(networkOption);

          setState({
            isConnected: true,
            address: activeAccount.address,
            balance: balance.toString(),
            network: networkOption,
            tezos: activeTezos,
            wallet: activeWallet,
          });
        } else {
          console.log('No active account found');
          setState(prev => ({ ...prev, tezos, wallet }));
        }
      } catch (error) {
        console.error('Failed to initialize wallet:', error);
      }
    };

    initWallet();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connect = useCallback(async () => {
    if (!state.wallet || !state.tezos) {
      console.error('Wallet or Tezos not initialized');
      return;
    }

    setIsLoading(true);
    try {
      console.log('Requesting wallet permissions...');

      // Request permissions (network is set when wallet is created)
      await state.wallet.requestPermissions();

      // Use getPKH() to get address - more reliable than event listeners
      const address = await state.wallet.getPKH();
      console.log('Connected to:', address);

      const balance = await state.tezos.tz.getBalance(address);
      setState(prev => ({
        ...prev,
        isConnected: true,
        address,
        balance: balance.toString(),
      }));
    } catch (error: any) {
      console.log('Wallet connection cancelled or failed:', error?.message);
    } finally {
      setIsLoading(false);
    }
  }, [state.wallet, state.tezos]);

  const disconnect = useCallback(async () => {
    if (!state.wallet) return;

    setIsLoading(true);
    try {
      await state.wallet.clearActiveAccount();
      setState(prev => ({
        ...prev,
        isConnected: false,
        address: null,
        balance: null,
        network: null,
      }));
    } catch (error) {
      console.error('Failed to disconnect wallet:', error);
    } finally {
      setIsLoading(false);
    }
  }, [state.wallet]);

  const refreshBalance = useCallback(async () => {
    if (!state.tezos || !state.address) return;

    try {
      const balance = await state.tezos.tz.getBalance(state.address);
      setState(prev => ({
        ...prev,
        balance: balance.toString(),
      }));
    } catch (error) {
      console.error('Failed to refresh balance:', error);
    }
  }, [state.tezos, state.address]);

  const switchNetwork = useCallback(async (network: NetworkOption) => {
    const config = NETWORK_CONFIG[network];
    console.log(`Connecting to ${network}...`, config);

    setIsLoading(true);
    try {
      // Create fresh wallet for the new network
      const wallet = await resetWalletForNetwork(network);
      const newTezos = new TezosToolkit(config.rpcUrl);
      newTezos.setWalletProvider(wallet);

      // Request permissions (network is set when wallet is created)
      await wallet.requestPermissions();

      // Use getPKH() to get address - more reliable than event listeners
      const address = await wallet.getPKH();
      console.log('Connected to:', address, 'on network:', network);

      const balance = await newTezos.tz.getBalance(address);

      // Sync the app network on successful connection
      setAppNetwork(network);

      setState({
        isConnected: true,
        address,
        balance: balance.toString(),
        network: network, // Use our NetworkOption, not Beacon's type
        tezos: newTezos,
        wallet,
      });
    } catch (error: any) {
      console.log('Network switch cancelled or failed:', error?.message);
      setState(prev => ({
        ...prev,
        isConnected: false,
        address: null,
        balance: null,
        network: null,
      }));
    } finally {
      setIsLoading(false);
    }
  }, [setAppNetwork]);

  return {
    ...state,
    isLoading,
    connect,
    disconnect,
    refreshBalance,
    switchNetwork,
  };
};
